"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  setGuestCareerStage,
  type CareerStageValue,
} from "@/lib/guestCareerStage";

const OPTIONS: Array<{
  value: CareerStageValue;
  title: string;
  description: string;
}> = [
  {
    value: "new_grad",
    title: "就活（新卒・第二新卒）",
    description: "これから社会人になる方・第二新卒の方向けの質問で練習します",
  },
  {
    value: "mid_career",
    title: "転職（社会人経験あり）",
    description: "すでに働いた経験がある方向けの質問で練習します",
  },
];

interface Props {
  // 選択後に進む練習体験の遷移先。既定は未ログインの単発体験（固定質問1問）。
  nextHref?: string;
}

// 未ログインユーザー向けの練習タイプ確認画面。
// 2択のいずれかを選ぶと練習タイプをブラウザに一時保存し、練習体験へ進める。未選択では進めない。
export default function GuestCareerStageSelect({
  nextHref = "/practice?questionId=1",
}: Props) {
  const router = useRouter();
  const [selected, setSelected] = useState<CareerStageValue | null>(null);

  const handleProceed = () => {
    if (!selected) return;
    setGuestCareerStage(selected);
    router.push(nextHref);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">まずは練習タイプを選びましょう</h1>
          <p className="text-sm leading-relaxed">
            あなたの状況に合わせて、質問とフィードバックの内容が変わります。
          </p>
        </div>

        <div className="space-y-4 mb-8" role="radiogroup" aria-label="練習タイプの選択">
          {OPTIONS.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left py-6 px-6 rounded-2xl border bg-white transition-all min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isSelected
                    ? "border-2 border-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <span
                  className={`block text-xl font-bold ${
                    isSelected ? "text-primary" : "text-on-surface"
                  }`}
                >
                  {opt.title}
                </span>
                <span className="block text-sm mt-2 leading-relaxed">
                  {opt.description}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleProceed}
          disabled={!selected}
          className="w-full bg-gradient-to-r from-primary to-accent text-white font-bold text-base rounded-xl min-h-[56px] px-6 transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          練習を始める
        </button>
      </div>
    </div>
  );
}
